import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, MessageSquare, Users, Zap, RefreshCw, HeadphonesIcon, CheckSquare, Square, Loader2, CheckCircle, ArrowRight, Globe, Mic, PlugZap, TrendingUp, Star, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import PhoneInput from '../components/landing/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: 'easeOut', delay }} className={className}>
      {children}
    </motion.div>
  );
}

const USE_CASES = [
  { id: 'inbound_sales', label: 'Inbound Sales', icon: <Phone size={15} />, desc: 'Answer leads the moment they call' },
  { id: 'outbound_sales', label: 'Outbound Sales', icon: <TrendingUp size={15} />, desc: 'Proactive outreach at scale' },
  { id: 'customer_support', label: 'Customer Support', icon: <HeadphonesIcon size={15} />, desc: '24/7 support without added headcount' },
  { id: 'reactivation', label: 'Reactivation Agents', icon: <RefreshCw size={15} />, desc: 'Re-engage cold and dormant contacts' },
  { id: 'appointments', label: 'Appointment Setting', icon: <Clock size={15} />, desc: 'Qualify and schedule automatically' },
  { id: 'other', label: 'Other', icon: <Zap size={15} />, desc: 'Tell us what you have in mind' },
];


const AI_CAPABILITIES = [
  { id: 'custom_agents', label: 'Custom AI Agents', desc: 'Automate specific business processes' },
  { id: 'workflow_automation', label: 'Workflow Automation', desc: 'AI-powered process automation' },
  { id: 'custom_chatbot', label: 'Custom Chatbot / LLM', desc: 'Built for your business knowledge' },
  { id: 'ai_analytics', label: 'AI Analytics & Reporting', desc: 'Insights from your data' },
  { id: 'document_processing', label: 'Document & Data Processing', desc: 'Extract and process unstructured data' },
  { id: 'not_at_this_time', label: 'Not at this time', desc: "I'll explore this later" },
  { id: 'other_ai', label: 'Other', desc: 'Tell us what you have in mind' },
];

function CapCheckbox({ checked, onChange, label, desc }) {
  return (
    <button type="button" onClick={onChange}
      className="flex items-start gap-2.5 p-3 rounded-xl text-left w-full transition-all duration-150 border"
      style={{
        borderColor: checked ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)',
        background: checked ? 'rgba(201,168,76,0.07)' : 'rgba(255,255,255,0.02)',
      }}>
      <div className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all"
        style={{ borderColor: checked ? '#C9A84C' : 'rgba(255,255,255,0.25)', background: checked ? 'rgba(201,168,76,0.15)' : 'transparent' }}>
        {checked && (
          <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
            <path d="M1 3.5l2 2 4-4" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div>
        <div className="text-xs font-semibold" style={{ color: checked ? '#fff' : 'rgba(255,255,255,0.65)' }}>{label}</div>
        {desc && <div className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{desc}</div>}
      </div>
    </button>
  );
}

function VoiceForm() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', cellPhone: '', businessUrl: '',
  });
  const [useCases, setUseCases] = useState([]);
  const [aiCapabilities, setAiCapabilities] = useState([]);
  const [phoneValid, setPhoneValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleUseCase = (id) => {
    setUseCases((prev) => prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]);
    if (errors.useCases) setErrors((p) => ({ ...p, useCases: '' }));
  };

  const toggleCap = (id) => {
    setAiCapabilities(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.cellPhone.trim()) e.cellPhone = 'Required';
    else if (!phoneValid) e.cellPhone = 'Please enter a valid phone number';
    if (!form.businessUrl.trim()) e.businessUrl = 'Required';
    if (useCases.length === 0) e.useCases = 'Select at least one';
    if (aiCapabilities.length === 0) e.aiCapabilities = 'Select at least one';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/voice-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, useCases, aiCapabilities }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success("You're on the list!", { description: 'Our team will reach out shortly.' });
      } else {
        const msg = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join(', ') : String(data.detail || 'Something went wrong');
        toast.error('Submission failed', { description: msg });
      }
    } catch { toast.error('Network error', { description: 'Please try again.' }); }
    finally { setLoading(false); }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl text-white text-sm placeholder:text-white/35 border border-white/15 bg-black/50 input-gold outline-none transition-colors duration-200';
  const labelCls = 'block text-xs font-medium text-white/70 mb-1.5';
  const errCls = 'text-xs mt-1 text-red-400';

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-[#C9A84C]/30 bg-white/[0.04] p-10 text-center"
        style={{ backdropFilter: 'blur(12px)' }}>
        <div className="flex justify-center mb-5">
          <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)' }}>
            <CheckCircle size={38} style={{ color: '#C9A84C' }} />
          </div>
        </div>
        <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>You&apos;re in.</h3>
        <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto">
          Our team will reach out within 24 hours to discuss your use case and get you set up.
        </p>
        <div className="mt-6 text-xs text-white/35 font-mono-accent tracking-widest">SALIBALABS.AI — VOICE PLATFORM</div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-7"
      style={{ backdropFilter: 'blur(16px)', boxShadow: '0 0 0 1px rgba(201,168,76,0.08), 0 32px 80px rgba(0,0,0,0.6)' }}>

      <div className="mb-6">
        <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-1">Request Access</div>
        <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Get Started with Voice</h3>
        <p className="text-xs text-white/50 mt-1">Tell us about your business and we&apos;ll reach out within 24 hours.</p>
      </div>

      <div className="space-y-4">
        {/* First + Last */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>First Name <span style={{color:'#C9A84C'}}>*</span></label>
            <input name="firstName" value={form.firstName} onChange={handleChange}
              placeholder="Alex" className={inputCls} autoComplete="given-name" />
            {errors.firstName && <p className={errCls}>{errors.firstName}</p>}
          </div>
          <div>
            <label className={labelCls}>Last Name <span style={{color:'#C9A84C'}}>*</span></label>
            <input name="lastName" value={form.lastName} onChange={handleChange}
              placeholder="Johnson" className={inputCls} autoComplete="family-name" />
            {errors.lastName && <p className={errCls}>{errors.lastName}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className={labelCls}>Work Email <span style={{color:'#C9A84C'}}>*</span></label>
          <input name="email" type="email" value={form.email} onChange={handleChange}
            placeholder="alex@yourcompany.com" className={inputCls} autoComplete="email" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>

        {/* Phone + Business URL */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <PhoneInput
              value={form.cellPhone}
              onChange={(full, digits, country) => {
                setForm(p => ({ ...p, cellPhone: full }));
                setPhoneValid(digits.length >= country.minDigits && digits.length <= country.maxDigits);
                if (errors.cellPhone) setErrors(p => ({ ...p, cellPhone: '' }));
              }}
              error={errors.cellPhone}
              required
              testId="voice-cell-phone-input"
            />
          </div>
          <div>
            <label className={labelCls}>Business URL <span style={{color:'#C9A84C'}}>*</span></label>
            <input name="businessUrl" value={form.businessUrl} onChange={handleChange}
              placeholder="yourcompany.com" className={inputCls} />
            {errors.businessUrl && <p className={errCls}>{errors.businessUrl}</p>}
          </div>
        </div>

        {/* Use Cases checklist */}
        <div>
          <label className={labelCls}>What are you looking to deploy? <span style={{ color: '#C9A84C' }}>*</span></label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {USE_CASES.map((uc) => {
              const checked = useCases.includes(uc.id);
              return (
                <button key={uc.id} type="button" onClick={() => toggleUseCase(uc.id)}
                  className="flex items-start gap-2.5 p-3 rounded-xl text-left transition-all duration-150 border"
                  style={{
                    borderColor: checked ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)',
                    background: checked ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)',
                  }}>
                  <span className="mt-0.5 flex-shrink-0" style={{ color: checked ? '#C9A84C' : 'rgba(255,255,255,0.3)' }}>
                    {checked ? <CheckSquare size={15} /> : <Square size={15} />}
                  </span>
                  <div>
                    <div className="text-xs font-semibold" style={{ color: checked ? '#fff' : 'rgba(255,255,255,0.65)' }}>{uc.label}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{uc.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
          {errors.useCases && <p className={errCls}>{errors.useCases}</p>}
        </div>
      </div>

      {/* AI Capabilities */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-white/70">Additional AI capabilities? <span style={{color:'#C9A84C'}}>*</span></label>
          <span className="text-[10px] text-white/35 font-mono-accent">Select all that apply</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {AI_CAPABILITIES.map(cap => (
            <CapCheckbox
              key={cap.id}
              checked={aiCapabilities.includes(cap.id)}
              onChange={() => toggleCap(cap.id)}
              label={cap.label}
              desc={cap.desc}
            />
          ))}
        </div>
        {errors.aiCapabilities && <p className="text-xs mt-1 text-red-400">{errors.aiCapabilities}</p>}
      </div>

      <button type="submit" disabled={loading}
        className="w-full mt-6 py-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200"
        style={{ background: loading ? 'rgba(201,168,76,0.5)' : '#C9A84C', color: '#0a0a0a', cursor: loading ? 'not-allowed' : 'pointer' }}
        data-testid="voice-waitlist-submit-button">
        {loading ? <><Loader2 size={16} className="animate-spin" /> Submitting&hellip;</> : <>Get Started <ArrowRight size={15} /></>}
      </button>
      <p className="text-center text-xs text-white/35 mt-3">Our team responds within 24 hours.</p>
    </form>
  );
}

// ── VOICE NAVBAR ─────────────────────────────────────────────────────────────
function VoiceNav({ onCtaClick }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/70 backdrop-blur-md border-b border-white/10 shadow-lg' : 'bg-transparent'
    }`}>
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="hidden sm:flex items-center gap-1 text-xs text-white/40 hover:text-white/70 transition-colors mr-1">
            ← Home
          </a>
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">Saliba</span><span style={{ color: '#C9A84C' }}>Labs</span><span className="text-white/50">.ai</span>
          </span>
          <span className="hidden sm:block px-2.5 py-0.5 rounded-full text-[10px] font-mono-accent tracking-widest border border-[#C9A84C]/30 text-[#C9A84C]">VOICE PLATFORM</span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {[['How It Works', '#how'], ['Capabilities', '#capabilities'], ['For Automotive', '/auto']].map(([label, href]) => (
            <a key={label} href={href}
              className="text-sm text-white/70 hover:text-white transition-colors relative group">
              {label}
              <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-0 group-hover:w-full h-px bg-[#C9A84C]/60 transition-all duration-200" />
            </a>
          ))}
        </div>
        <button onClick={onCtaClick}
          className="text-sm font-semibold px-5 py-2.5 rounded-xl transition-all duration-200 hover:shadow-[0_0_20px_rgba(201,168,76,0.4)]"
          style={{ background: '#C9A84C', color: '#0a0a0a' }}>
          Request Info
        </button>
      </div>
    </nav>
  );
}

// ── VOICE HERO ────────────────────────────────────────────────────────────────
function VoiceHero({ formRef }) {
  return (
    <section className="relative min-h-screen flex items-center pt-16"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/18572796/pexels-photo-18572796.jpeg?auto=compress&cs=tinysrgb&w=940)',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(10,10,10,0.90) 50%, rgba(0,0,0,0.78) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(800px circle at 20% 50%, rgba(201,168,76,0.10), transparent 55%)' }} />

      <div className="relative z-10 mx-auto w-[min(1120px,calc(100%-32px))] py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: 'easeOut' }}>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/8">
              <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
              <span className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase">SalibaLabs.ai • Voice Platform</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-semibold leading-[1.08] tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>
              The voice agent
              <br />
              your business
              <br />
              <span className="gold-text">has been waiting for.</span>
            </h1>

            <p className="text-base sm:text-lg text-white/65 leading-relaxed mb-8 max-w-xl">
              Every call answered. Every lead followed up. Every customer supported —
              in their language, on their channel, at any hour.
              One agent. <span className="text-white font-medium">Infinite conversations.</span>
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              {[
                { icon: <Zap size={13} />, label: '<500ms Response' },
                { icon: <Globe size={13} />, label: '10+ Languages' },
                { icon: <Phone size={13} />, label: '10K+ Concurrent Calls' },
              ].map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-sm text-white/70">
                  <span style={{ color: '#C9A84C' }}>{c.icon}</span>{c.label}
                </div>
              ))}
            </div>

            <div className="flex gap-8">
              {[{ v: '<500ms', l: 'Time to First Audio' }, { v: '10K+', l: 'Concurrent Calls' }, { v: '10+', l: 'Native Languages' }].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold" style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}>{s.v}</div>
                  <div className="text-xs text-white/50 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div ref={formRef} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.15 }}>
            <VoiceForm />
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to bottom, transparent, #0a0a0a)' }} />
    </section>
  );
}

// ── TRUSTED BY ────────────────────────────────────────────────────────────────
// ── USE CASES SECTION ─────────────────────────────────────────────────────────
function VoiceUseCases() {
  const cases = [
    {
      icon: <Phone size={28} />,
      title: 'Inbound Sales',
      desc: 'Every inbound call answered instantly. Your agent qualifies the lead, answers objections, and books the appointment — so your team shows up to close.',
      bullets: ['Instant answer, no hold times', 'Lead qualification + scoring', 'Appointment booking', 'CRM-synced automatically'],
    },
    {
      icon: <TrendingUp size={28} />,
      title: 'Outbound Sales',
      desc: 'Launch outbound campaigns at scale without burning out your team. Personalized voice outreach, real conversations, real results.',
      bullets: ['Personalized at scale', 'Dynamic script logic', 'Handles objections naturally', 'Dispositions every call'],
    },
    {
      icon: <HeadphonesIcon size={28} />,
      title: 'Customer Support',
      desc: '24/7 support that actually resolves issues. Your agent pulls from your knowledge base in real time — sub-100ms retrieval.',
      bullets: ['24/7 availability', 'Knowledge base integration', 'Escalates to humans when needed', 'Full conversation logging'],
    },
    {
      icon: <RefreshCw size={28} />,
      title: 'Reactivation Agents',
      desc: 'Cold contacts. Lapsed customers. Dead leads. Your agent finds them, reaches out, and brings them back into the pipeline.',
      bullets: ['CRM-powered targeting', 'Personalized re-engagement', 'Multi-touch sequences', 'Converts cold to warm'],
    },
  ];
  return (
    <section id="capabilities" className="section-anchor py-24 lg:py-32" style={{ background: '#0e0e0f' }}>
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16 lg:pt-24">
        <Reveal>
          <div className="text-center mb-16">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">Use Cases</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>One agent. Every conversation type.</h2>
            <p className="text-white/55 mt-4 max-w-lg mx-auto">Deploy for inbound, outbound, support, or reactivation — or all four. Same agent, same infrastructure, fully configurable.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {cases.map((c, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="card-hover rounded-2xl border border-white/8 bg-white/[0.025] p-7">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C' }}>{c.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{c.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed mb-4">{c.desc}</p>
                <ul className="space-y-1.5">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/65">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#C9A84C' }} />{b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── HOW IT WORKS ──────────────────────────────────────────────────────────────
function VoiceHowItWorks() {
  const steps = [
    { n: '01', icon: <Mic size={22} />, title: 'One integration', desc: 'Connect your phone number, CRM, and knowledge base. We handle the infrastructure — SIP, speech models, retrieval — end to end.', badge: 'Setup in days' },
    { n: '02', icon: <Zap size={22} />, title: 'Agent deployed', desc: 'Your voice agent goes live — answering calls, making outbound dials, and handling every conversation with sub-500ms response time.', badge: '<500ms latency' },
    { n: '03', icon: <PlugZap size={22} />, title: 'Synced everywhere', desc: 'Every call summarized, every lead dispositioned, every interaction pushed into your CRM automatically. Full pipeline visibility.', badge: 'CRM synced' },
  ];
  return (
    <section id="how" className="section-anchor py-24 lg:py-32">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="text-center mb-16">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">How It Works</div>
            <h2 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>From zero to live in days.</h2>
          </div>
        </Reveal>
        <div className="grid lg:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)', color: '#C9A84C' }}>{s.icon}</div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold font-mono-accent" style={{ background: '#C9A84C', color: '#0a0a0a' }}>{s.n}</div>
                </div>
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold font-mono-accent tracking-widest uppercase mb-3" style={{ background: 'rgba(201,168,76,0.1)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.2)' }}>{s.badge}</div>
                <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>{s.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── INTEGRATIONS ──────────────────────────────────────────────────────────────
// ── THE LATENCY SECTION ────────────────────────────────────────────────────
function LatencySection() {
  const metrics = [
    {
      value: '<500ms',
      label: 'Time to First Audio',
      desc: 'From the moment a customer speaks to the moment your agent responds. Most platforms are 2–4 seconds. We\'re under half a second.',
      highlight: true,
    },
    {
      value: '<3s',
      label: 'End-to-End Round Trip',
      desc: 'Total time for a message to leave the server, reach the customer, and return. We keep both the response time and delay time under three seconds — always.',
      highlight: false,
    },
    {
      value: '∞',
      label: 'Context Memory',
      desc: 'Your agent remembers the full conversation — not just the last message. No dropped context. No "sorry, can you repeat that?" moments.',
      highlight: false,
    },
    {
      value: 'Tier 1',
      label: 'Server Infrastructure',
      desc: 'We don\'t cut corners on processing power. Every deployment runs on premium infrastructure — because your customer\'s experience depends on it.',
      highlight: false,
    },
  ];

  return (
    <section className="section-anchor py-24 lg:py-32 relative overflow-hidden" style={{ background: '#080808' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)' }} />

      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="mb-16">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-4">The Conversation Nobody Has</div>
            <div className="grid lg:grid-cols-2 gap-8 items-end">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4" style={{ fontFamily: 'Space Grotesk' }}>
                  Let's talk about latency.
                  <br />
                  <span className="gold-text">Most platforms won't.</span>
                </h2>
                <p className="text-white/60 text-base leading-relaxed">
                  Latency is the time it takes for a message to travel from the server to your customer.
                  There are two types — response time and delay time — and you want both under three seconds.
                  Drop below that threshold and you get a natural, human-feeling conversation.
                  Go above it and you lose the customer.
                </p>
              </div>
              <div className="lg:text-right">
                <p className="text-white/50 text-sm leading-relaxed italic border-l-2 border-[#C9A84C]/40 pl-4 lg:border-l-0 lg:border-r-2 lg:pr-4 lg:pl-0">
                  &ldquo;We don&rsquo;t get cheap on the processing power.
                  We only deploy on the best servers to reduce latency —
                  so your customers get the best experience.&rdquo;
                </p>
                <p className="font-mono-accent text-xs text-[#C9A84C] mt-2 lg:text-right">— SalibaLabs Infrastructure Principle</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Metrics grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {metrics.map((m, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div
                className="rounded-2xl p-6 h-full flex flex-col card-hover"
                style={{
                  border: m.highlight ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.07)',
                  background: m.highlight ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)',
                  boxShadow: m.highlight ? '0 0 40px rgba(201,168,76,0.08)' : 'none',
                }}
              >
                <div
                  className="text-4xl font-bold mb-3 tracking-tight"
                  style={{ color: '#C9A84C', fontFamily: 'Space Grotesk' }}
                >
                  {m.value}
                </div>
                <div className="text-sm font-semibold text-white mb-2">{m.label}</div>
                <div className="text-xs text-white/50 leading-relaxed flex-1">{m.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Memory explanation */}
        <Reveal delay={0.2}>
          <div
            className="rounded-2xl p-8 lg:p-10 relative overflow-hidden"
            style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)' }}
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">The Memory Problem</div>
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
                  Context is everything.<br />Most agents lose it.
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  Here&rsquo;s what nobody talks about: when a server runs out of memory to hold a conversation,
                  the agent starts losing context. It forgets what was said earlier.
                  The responses get slower. Then they get wrong.
                </p>
                <p className="text-white/60 text-sm leading-relaxed">
                  We solve this by deploying on infrastructure with the capacity to hold the full conversation
                  in memory — from the first word to the last. Your agent always knows the full picture.
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Full conversation held in memory', good: true },
                  { label: 'No context loss mid-conversation', good: true },
                  { label: 'No "sorry, can you repeat that?"', good: true },
                  { label: 'Premium servers — no cheap shortcuts', good: true },
                  { label: 'Latency monitored in real time', good: true },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.07 }}
                    className="flex items-center gap-3 p-3.5 rounded-xl border border-white/6 bg-white/[0.02]"
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.15)' }}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2 2 4-4" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm text-white/75">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function VoiceIntegrations() {
  const integrations = ['Salesforce', 'HubSpot', 'Zendesk', 'Twilio', 'Google', 'Microsoft', 'Intercom', 'Pipedrive', 'Zoho', 'Monday', 'Cal.com', 'Airtable'];
  return (
    <section className="section-anchor py-20 lg:py-24" style={{ background: '#0e0e0f' }}>
      <div className="gold-divider mb-0" />
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] pt-16">
        <Reveal>
          <div className="text-center mb-10">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">Integrations</div>
            <h2 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Plugs into what you already run.</h2>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-3 justify-center">
            {integrations.map((name, i) => (
              <div key={i} className="px-4 py-2.5 rounded-xl border border-white/8 bg-white/[0.02] text-sm text-white/60 font-medium">{name}</div>
            ))}
            <div className="px-4 py-2.5 rounded-xl border border-[#C9A84C]/20 text-sm font-medium" style={{ color: '#C9A84C', background: 'rgba(201,168,76,0.05)' }}>+1000s more</div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ── FINAL CTA ─────────────────────────────────────────────────────────────────
function VoiceFinalCTA() {
  return (
    <section className="section-anchor py-24 lg:py-32">
      <div className="mx-auto w-[min(1120px,calc(100%-32px))]">
        <Reveal>
          <div className="text-center mb-12">
            <div className="font-mono-accent text-xs text-[#C9A84C] tracking-widest uppercase mb-3">Get Started</div>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Ready to deploy your voice agent?</h2>
            <p className="text-white/55 max-w-lg mx-auto">Fill out the form and our team will reach out within 24 hours to walk you through setup.</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="max-w-xl mx-auto"><VoiceForm /></div>
        </Reveal>
      </div>
    </section>
  );
}

// ── VOICE FOOTER ──────────────────────────────────────────────────────────────
function VoiceFooter() {
  return (
    <footer className="border-t border-white/8 py-10" style={{ background: '#080808' }}>
      <div className="mx-auto w-[min(1120px,calc(100%-32px))] flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <div className="text-base font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            <span className="text-white">Saliba</span><span style={{ color: '#C9A84C' }}>Labs</span><span className="text-white/40">.ai</span>
          </div>
          <div className="text-xs text-white/40">AI built for businesses that never stop growing.</div>
        </div>
        <div className="flex items-center gap-6">
          <a href="/auto" className="text-xs text-white/50 hover:text-white transition-colors">Automotive →</a>
          <a href="/" className="text-xs text-white/50 hover:text-white transition-colors">← Home</a>
        </div>
        <div className="text-xs text-white/30">&copy; {new Date().getFullYear()} SalibaLabs.ai. All rights reserved.</div>
      </div>
    </footer>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function VoicePage() {
  const formRef = useRef(null);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a', color: '#fafafa' }}>
      <VoiceNav onCtaClick={scrollToForm} />
      <VoiceHero formRef={formRef} />
      <VoiceUseCases />
      <VoiceHowItWorks />
      <LatencySection />
      <VoiceIntegrations />
      <VoiceFinalCTA />
      <VoiceFooter />
    </div>
  );
}
